import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PortalLayout from '@/components/feature/PortalLayout';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DetailPage() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareTip, setShareTip] = useState('');

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .map((m) => {
        let score = 0;
        if (m.type === movie.type) score += 1;
        score += m.genres.filter((g) => movie.genres.includes(g)).length * 2;
        score += m.cast.filter((c) => movie.cast.includes(c)).length * 3;
        return { m, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((x) => x.m);
  }, [movie]);

  usePageMeta({
    title: movie ? `影视详情 - ${movie.title} - 诱人的女员工hd` : '影视详情 - 诱人的女员工hd',
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）剧情简介：${movie.synopsis.slice(0, 60)}… 主演：${movie.cast.join('、')}。`
      : '影视详情页',
    keywords: movie ? `诱人的女员工hd,${movie.title},剧情介绍,演员信息` : '诱人的女员工hd,影视详情',
  });

  if (!movie) {
    return (
      <PortalLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-4">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="font-heading font-black text-2xl text-foreground-950">找不到这部影视作品</h1>
          <p className="mt-2 text-sm text-foreground-500">该作品可能已被移除，去首页看看其他推荐吧</p>
          <Link to="/" className="inline-block mt-6 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </PortalLayout>
    );
  }

  const fav = isFavorite(movie.id);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareTip('链接已复制到剪贴板');
    } catch {
      setShareTip('复制失败，请手动复制地址栏链接');
    }
    window.setTimeout(() => setShareTip(''), 2200);
  };

  const paragraphs = movie.synopsis.split('。').filter(Boolean);

  return (
    <PortalLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <nav className="flex items-center gap-1 text-xs text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-accent-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={movie.type === 'tv' ? '/category/tv' : '/category/movie'} className="hover:text-accent-600 cursor-pointer">
            {movie.type === 'tv' ? '电视剧' : '电影'}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">{movie.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-10">
          {/* cover */}
          <div className="w-48 sm:w-56 lg:w-full mx-auto lg:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-900 border border-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title}高清剧照`}
                title={`${movie.title} - 诱人的女员工hd影视专区`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* info */}
          <div className="min-w-0">
            <div className="flex items-start gap-3 flex-wrap">
              <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">{movie.title}</h1>
              {movie.rating > 0 && (
                <span className="mt-1 px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-sm font-bold">
                  {movie.rating.toFixed(1)} 分
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              {movie.genres.map((g) => (
                <span key={g} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                  {g}
                </span>
              ))}
              <span className="px-2.5 py-1 rounded-md bg-background-200 text-foreground-600 text-xs">
                {movie.type === 'movie' ? '电影' : '剧集'}
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex gap-2">
                <dt className="text-foreground-500 shrink-0">导演</dt>
                <dd className="text-foreground-900">{movie.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-500 shrink-0">地区</dt>
                <dd className="text-foreground-900">{movie.region}</dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="text-foreground-500 shrink-0">主演</dt>
                <dd className="text-foreground-900">{movie.cast.join('、')}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-500 shrink-0">上映</dt>
                <dd className="text-foreground-900">{movie.releaseDate}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-500 shrink-0">年份</dt>
                <dd className="text-foreground-900">{movie.year} 年</dd>
              </div>
            </dl>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  fav ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-800 hover:bg-background-300'
                }`}
              >
                <i className={`${fav ? 'ri-heart-3-fill' : 'ri-heart-3-line'} mr-1`}></i>
                {fav ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-accent-400 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line mr-1"></i>分享链接
              </button>
              {shareTip && <span className="text-xs text-secondary-700">{shareTip}</span>}
            </div>
          </div>
        </div>

        {/* synopsis */}
        <section className="mt-10">
          <h2 className="font-heading font-black text-xl text-foreground-950 mb-3">剧情简介</h2>
          <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
            <div className="space-y-3 text-sm md:text-base text-foreground-700 leading-loose">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}。</p>
              ))}
            </div>
          </div>
        </section>

        {/* related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading font-black text-xl text-foreground-950 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </PortalLayout>
  );
}